import { attr, customElement, FASTElement, observable } from '@microsoft/fast-element';
import { WatchlistProjectCardTemplate as template } from './watchlist-project-card.template';
import { WatchlistProjectCardStyles as styles } from './watchlist-project-card.styles';
import { WatchlistProject } from '../../../types';
import { IProjectService, IVppaService } from '../../../services';
import { ZeroGridPro as zeroAgGrid } from '@genesislcap/foundation-zero-grid-pro';
import { WATCHLIST_ENTITY_TYPE } from '../../../utils/enums/generic.enum';
import { IIndividualProjectPageService } from '../../../services/individual-project-page.service';
import { Message, SocketSubscription } from '@genesislcap/foundation-comms';
import { updateArray } from '../../../utils/dataserver';

const name = 'watchlist-project-card';

interface RemoveObject {
  NAME: string;
  WATCHLIST_ENTITY_ID: string;
  WATCHLIST_ENTITY_ID_TYPE: string;
}

@customElement({
  name,
  template,
  styles,
})
export class WatchlistProjectCard extends FASTElement {
  @attr @observable project: WatchlistProject;
  @IProjectService projectService: IProjectService;
  @IIndividualProjectPageService individualProjectPageService: IIndividualProjectPageService;
  @IVppaService vppaService: IVppaService;
  @observable renderWatchlistCard: boolean = true;
  @observable gridVisibility: boolean = true;
  @observable vppaGrid: zeroAgGrid;
  @observable numberOfRows: number = 0;
  @observable gridHeight: string = '34px';
  @observable removeObject: RemoveObject;
  @observable projectWatchlistSubscription: SocketSubscription;
  @observable watchlistVppas = [];
  favoriteBtn: HTMLButtonElement;

  connectedCallback() {
    super.connectedCallback();

    const gridOptions = {
      onRowClicked: (row) => this.rowSelection(row),
      onRowSelected: (params) => this.rowSelected(params),
      groupRowRendererParams: {
        checkbox: true,
        suppressCount: true,
      },
      animateRows: true,
      groupSelectsChildren: true,
      scrollbarWidth: 10,
      suppressRowClickSelection: true,
      suppressBrowserResizeObserver: true,
      rowClass: 'transparent-background',
    };

    this.vppaGrid.gridOptions = gridOptions;
  }

  public projectChanged(oldValue, newValue) {
    if (newValue && oldValue == undefined) {
      this.getWatchlistVppas(newValue);
    }
  }

  public switchCardsForDeletion() {
    this.renderWatchlistCard = !this.renderWatchlistCard;
    this.removeObject = {
      NAME: this.project.PROJECT_NAME,
      WATCHLIST_ENTITY_ID: this.project.WATCHLIST_ENTITY_ID,
      WATCHLIST_ENTITY_ID_TYPE: this.project.WATCHLIST_ENTITY_ID_TYPE,
    };
  }

  public switchCardsForVppaDeletion(data) {
    this.renderWatchlistCard = !this.renderWatchlistCard;
    this.removeObject = {
      NAME: data.TICKER,
      WATCHLIST_ENTITY_ID: data.WATCHLIST_ENTITY_ID,
      WATCHLIST_ENTITY_ID_TYPE: data.WATCHLIST_ENTITY_ID_TYPE,
    };
  }

  public cancelDeletion() {
    this.renderWatchlistCard = !this.renderWatchlistCard;
  }

  public async deleteProjectFromWatchlist(): Promise<any> {
    if (this.removeObject.WATCHLIST_ENTITY_ID_TYPE === WATCHLIST_ENTITY_TYPE.PROJECT) {
      this.projectService.eventDeleteFavoritedProject(this.removeObject.WATCHLIST_ENTITY_ID);
    } else if (this.removeObject.WATCHLIST_ENTITY_ID_TYPE === WATCHLIST_ENTITY_TYPE.VPPA) {
      this.vppaService.eventDeleteFavoritedVppa(this.removeObject.WATCHLIST_ENTITY_ID);
    }

    this.renderWatchlistCard = !this.renderWatchlistCard;
  }

  public switchGridVisibility() {
    this.gridVisibility = !this.gridVisibility;
  }

  public openProjectPage() {
    this.individualProjectPageService.openIndividualProjectPage(this.project?.WATCHLIST_ENTITY_ID);
  }

  public customEvents = {
    onRowDataUpdated: () => {
      const rowCount = this.vppaGrid.gridApi.getDisplayedRowCount();
      if (rowCount) {
        this.numberOfRows = rowCount;
        const gridHeightAux = 32.5 * this.numberOfRows + 34;
        this.gridHeight = gridHeightAux + 'px';
        return;
      }
      this.gridVisibility = false;
      this.numberOfRows = 0;
    },
  };

  public getWatchlistVppas(project) {
    let resultList = [];
    this.projectWatchlistSubscription = this.projectService
      .getAllWatchlistVPPAs("PROJECT_ID == '" + project?.WATCHLIST_ENTITY_ID + "'")
      .subscribe({
        next: (streamMessage: Message) => {
          resultList = streamMessage.ROW ?? null;
          if (resultList) {
            this.watchlistVppas = updateArray(resultList, this.watchlistVppas);
            this.vppaGrid.gridApi.setRowData(this.watchlistVppas);
            this.favoriteBtn.title = 'Remove from Watchlist';
          }
        },
        error: (error: any) => {},
      });
  }
  rowSelection(row) {
    if (row.data.WATCHLIST_ENTITY_ID) {
      this.$emit('vppaRowSelected', row.data.WATCHLIST_ENTITY_ID);
    }
  }

  public rowSelected(event) {
    if (event && event.node) {
      if (event.node.selected) {
        this.$emit('vppaRowSelected', event.data);
      }
    }
  }

  disconnectedCallback(): void {
    super.disconnectedCallback();

    this.projectWatchlistSubscription.unsubscribe();
  }
}
